import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
import anime from "animejs";
import { symbolData } from "../../SymbolData";
import { Settings } from "../../settings";
import { COIN_SYMBOL_IDS } from "../../SymbolData";
import store from "../../State/Store/store";
import { formatToCustomLocale } from "../../utilities";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";

/**
 * HoldAndWinOverlaySymbolController
 * Manages ONE coin spine animation for a single symbol position in Hold & Win
 * Each symbol position gets its own controller instance
 */
class HoldAndWinOverlaySymbolController {
  // Static variable to track last multiplier sound play time (shared across all instances)
  static lastMultiplierSoundTime = 0;
  static MULTIPLIER_SOUND_DEBOUNCE = 100; // Only play sound once per 100ms

  constructor(
    overlaySymbolContainer,
    reelIndex,
    symbolIndex,
    symbolWidth,
    symbolHeight
  ) {
    this.overlaySymbolContainer = overlaySymbolContainer;
    this.reelIndex = reelIndex;
    this.symbolIndex = symbolIndex;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    this.coinSpine = null; // Single coin spine instance
    this.isAnimating = false;
    this.animationCache = new Map();
    this.animationAvailability = new Set(); // ✅ Cache of available animations
    this.hasLanding = false;
    this.hasIdle = false;
    this.hasAppear = false;
    this.hasWin = false;

    // Animation state tracking (like main game)
    this.currentAnimationType = null;
    this.currentSymbolId = null;

    // Coin value text management
    this.coinValueContainer = null;
    this.coinValueBitmapText = null;
    this.coinValueTickerCallback = null;
    this.currentCoinValue = 0; // Track current value for animation
    this.coinValueAnimationRef = null; // Animation reference

    // Multiplier spine management
    this.multiplierSpine = null;
    this.multiplierAnimationCache = new Map();
    this.multiplierAnimationAvailability = new Set();
    this.currentMultiplierValue = null;
    this.isMultiplierAnimating = false;

    // Timeout tracking for cleanup
    this.activeTimeouts = [];

    this.initializeCoinSpine();
    this.initializeMultiplierSpine();
  }

  /**
   * Calculate display value for coin symbols (coinValue × bet)
   */
  _getCoinDisplayValue(symbolId) {
    const symbolValue = symbolData[symbolId]?.value;
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const state = store.getState();
      const bet = state?.game?.bet || 1;
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  /**
   * Create coin value text container with BitmapText
   * Uses current symbolHeight for proper responsive scaling
   * Implements progressive scaling based on digit count:
   * - 1-4 digits: Horizontal squishing only
   * - 5-8 digits: Horizontal squishing only (preserve height)
   * - 9+ digits: Uniform scaling (both width and height)
   */
  createCoinValueText(displayValue) {
    if (!displayValue || displayValue === "") {
      return null;
    }

    const counterContainer = new PIXI.Container();
    counterContainer.name = "coin_value_text";
    counterContainer.alpha = 1;
    counterContainer.zIndex = 15;

    // Calculate font size based on symbol height (use actual runtime height, not constant)
    const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;

    const bitmapText = new PIXI.BitmapText(displayValue, {
      fontName: "font",
      fontSize: fontSize,
    });

    bitmapText.anchor.set(0.5);
    bitmapText.x = 0;
    bitmapText.y = 0;
    bitmapText.zIndex = 1;
    bitmapText.roundPixels = true;

    // Force update to get accurate measurements
    bitmapText.updateText();

    // Calculate available width (with padding)
    const coinWidth = this.symbolWidth;
    const paddingPercent = 0.12; // 12% padding on each side
    const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

    const actualTextWidth = bitmapText.width;

    // Only apply scaling if text is too wide
    if (actualTextWidth > maxTextWidth) {
      // Count digits (excluding formatting characters like commas and dots)
      const digitCount = displayValue.replace(/[^0-9]/g, '').length;

      // Apply scaling method based on digit count
      if (digitCount <= 4) {
        // 1-4 digits: Horizontal squishing only
        const scaleX = maxTextWidth / actualTextWidth;
        bitmapText.scale.x = scaleX;
      } else if (digitCount >= 5 && digitCount <= 8) {
        // 5-8 digits: Horizontal squishing only (preserve height)
        const scaleX = maxTextWidth / actualTextWidth;
        bitmapText.scale.x = scaleX; // Only scale width
        // bitmapText.scale.y stays at 1.0 (preserve height)
      } else {
        // 9+ digits: Uniform scaling (both width and height)
        const scale = maxTextWidth / actualTextWidth;
        bitmapText.scale.set(scale); // Scale both X and Y uniformly
      }
    }

    counterContainer.addChild(bitmapText);

    return { counterContainer, bitmapText };
  }

  /**
   * Sync coin value text transform with spine's "coin_value" bone
   */
  updateCoinValueFromBone() {
    if (
      !this.coinSpine ||
      !this.coinValueContainer ||
      !this.coinSpine.skeleton
    ) {
      return;
    }

    const valueBone = this.coinSpine.skeleton.findBone("coin_value");
    if (!valueBone) {
      return;
    }

    this.coinValueContainer.x = valueBone.worldX;
    this.coinValueContainer.y = valueBone.worldY;
    this.coinValueContainer.rotation =
      (valueBone.getWorldRotationX() * Math.PI) / 180;

    // Apply bone's animation scale, but counteract the spine's base scale
    // This keeps bone animations working while maintaining correct text size
    const boneScaleX = valueBone.getWorldScaleX();
    const boneScaleY = valueBone.getWorldScaleY();
    const spineScale = this.coinSpine.scale.x; // Spine's base scale

    if (spineScale && spineScale !== 0) {
      // Bone scale divided by spine scale = animation-only scale at correct size
      this.coinValueContainer.scale.set(boneScaleX / spineScale, boneScaleY / spineScale);
    } else {
      this.coinValueContainer.scale.set(boneScaleX, boneScaleY);
    }
  }

  /**
   * Start bone update loop for coin value text
   */
  startCoinValueBoneUpdate() {
    this.coinValueTickerCallback = () => {
      this.updateCoinValueFromBone();
    };

    PIXI.Ticker.shared.add(this.coinValueTickerCallback);
    this.updateCoinValueFromBone();
  }

  /**
   * Stop bone update loop for coin value text
   */
  stopCoinValueBoneUpdate() {
    if (this.coinValueTickerCallback) {
      PIXI.Ticker.shared.remove(this.coinValueTickerCallback);
      this.coinValueTickerCallback = null;
    }
  }

  /**
   * Show coin value text for a coin symbol
   */
  showCoinValueText(symbolId) {
    this.hideCoinValueText();

    const displayValue = this._getCoinDisplayValue(symbolId);
    if (!displayValue) return;

    const result = this.createCoinValueText(displayValue);
    if (!result) return;

    this.coinValueContainer = result.counterContainer;
    this.coinValueBitmapText = result.bitmapText;

    // ✅ CRITICAL FIX: Add text as child of SPINE, not overlay container!
    // This matches main game behavior and ensures correct scaling
    if (this.coinSpine) {
      this.coinSpine.addChild(this.coinValueContainer);
      this.startCoinValueBoneUpdate();
    }
  }

  /**
   * Hide coin value text
   */
  hideCoinValueText() {
    this.stopCoinValueBoneUpdate();

    if (this.coinValueContainer) {
      if (this.coinValueContainer.parent) {
        this.coinValueContainer.parent.removeChild(this.coinValueContainer);
      }
      this.coinValueContainer.destroy({ children: true });
      this.coinValueContainer = null;
      this.coinValueBitmapText = null;
    }
  }

  /**
   * Fade out coin value text (set alpha to 0)
   * Used during collection - keeps the text object but makes it invisible
   */
  fadeCoinValueText() {
    if (this.coinValueContainer) {
      this.coinValueContainer.alpha = 0;
    }
  }

  /**
   * Create a single coin spine instance
   */
  createSpineInstance() {
    const coinSymbolId = COIN_SYMBOL_IDS[0]; // Use first coin ID
    const symbolInfo = symbolData[coinSymbolId];

    if (!symbolInfo?.spine || symbolInfo.spine === "") {
      return null;
    }

    const spineName = symbolInfo.spine;
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
      return null;
    }

    try {
      const spine = Spine.from({ skeleton: skeletonAlias, atlas: atlasAlias });
      spine.name = `coin_spine_${this.reelIndex}_${this.symbolIndex}`;
      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Calculate spine scale to fit symbol bounds
   * Copied from OverlayReelController for consistency
   */
  calculateSpineScale(
    spineInstance,
    containerWidth,
    containerHeight,
    skipAnimationSetup = false
  ) {
    if (
      !skipAnimationSetup &&
      spineInstance &&
      spineInstance.state &&
      spineInstance.state.data &&
      spineInstance.state.data.skeletonData.animations.length > 0
    ) {
      const defaultAnimation =
        spineInstance.state.data.skeletonData.animations[0];
      spineInstance.state.addAnimation(0, defaultAnimation.name, true, 0);
    }

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const widthScale = containerWidth / nativeWidth;
    const heightScale = containerHeight / nativeHeight;
    const scale = Math.min(widthScale, heightScale);

    const scaledWidth = nativeWidth * scale;
    const scaledHeight = nativeHeight * scale;
    const x = (containerWidth - scaledWidth) / 2;
    const y = (containerHeight - scaledHeight) / 2;

    return {
      scale,
      nativeWidth,
      nativeHeight,
      x,
      y,
      scaledWidth,
      scaledHeight,
    };
  }

  /**
   * Select animation by type (land/idle/appear/win)
   * Uses cached animation names for O(1) lookup
   */
  selectSpineAnimation(type) {
    if (!this.coinSpine?.state) return null;

    const cacheKey = type;
    if (this.animationCache.has(cacheKey)) {
      return this.animationCache.get(cacheKey);
    }

    const animations = this.coinSpine.skeleton?.data?.animations || [];
    const lowerType = type.toLowerCase();

    for (let i = 0; i < animations.length; i++) {
      const anim = animations[i];
      const animName = anim.name.toLowerCase();
      if (animName.includes(lowerType)) {
        this.animationCache.set(cacheKey, anim.name);
        return anim.name;
      }
    }

    return null;
  }

  /**
   * Check if animation type is available (uses cached availability)
   */
  hasAnimation(type) {
    const lowerType = type.toLowerCase();
    for (const animName of this.animationAvailability) {
      if (animName.includes(lowerType)) {
        return true;
      }
    }
    return false;
  }

  /**
   * Get available animations (like main game's getAvailableAnimations)
   */
  getAvailableAnimations() {
    return {
      hasLanding: this.hasLanding,
      hasIdle: this.hasIdle,
      hasAppear: this.hasAppear,
      hasWin: this.hasWin,
    };
  }

  /**
   * Warm up spine animation (shader compilation)
   * Matches main game's warmup pattern exactly
   */
  warmUpSpineAnimation(type) {
    if (!this.coinSpine?.state) return;

    const animName = this.selectSpineAnimation(type);
    if (!animName) return;

    try {
      this.coinSpine.state.clearTracks();
      this.coinSpine.state.addAnimation(0, animName, true, 0);
      this.coinSpine.state.update(0.016);

      this.coinSpine.alpha = 0.001;
      const overlaySymbol = this.coinSpine.parent;
      if (overlaySymbol) overlaySymbol.alpha = 0.001;

      requestAnimationFrame(() => {
        // CRITICAL: Only cleanup if no animation is currently active
        // This prevents warmup from hiding animations that were started after warmup
        if (this.isAnimating) {
          return; // Animation is active, don't cleanup
        }

        if (this.coinSpine?.state) this.coinSpine.state.clearTracks();
        if (this.coinSpine) this.coinSpine.alpha = 0.001;
        if (overlaySymbol) overlaySymbol.alpha = 0.001;
      });
    } catch (error) {
      // Silently ignore warmup errors
    }
  }

  /**
   * Smooth transition between animations (like main game)
   * Provides crossfade instead of hard cuts
   */
  _smoothTransitionToAnimation(newType, loop = true) {
    if (!this.coinSpine?.state) return false;

    const newAnimation = this.selectSpineAnimation(newType);
    if (!newAnimation) return false;

    try {
      const currentTracks = this.coinSpine.state.tracks;
      const hasCurrentAnimation =
        currentTracks && currentTracks.length > 0 && currentTracks[0];

      if (hasCurrentAnimation) {
        const currentTrack = currentTracks[0];
        const currentAnimationName = currentTrack.animation
          ? currentTrack.animation.name
          : null;

        if (currentAnimationName === newAnimation) return true;
        // Smooth crossfade with 0.1s mix duration
        this.coinSpine.state.addAnimation(0, newAnimation, loop, 0.1);
      } else {
        this.coinSpine.state.setAnimation(0, newAnimation, loop);
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Initialize the coin spine instance (hidden, ready to show)
   * Matches main game's initialization pattern exactly
   */
  initializeCoinSpine() {
    if (!this.overlaySymbolContainer) return;

    const coinSymbolId = COIN_SYMBOL_IDS[0];
    const symbolInfo = symbolData[coinSymbolId];

    if (!symbolInfo?.spine) {
      return;
    }

    this.coinSpine = this.createSpineInstance();
    if (!this.coinSpine) return;

    const scaleData = this.calculateSpineScale(
      this.coinSpine,
      this.symbolWidth,
      this.symbolHeight,
      true // Skip animation setup during initialization
    );

    this.coinSpine.position.set(scaleData.x, scaleData.y);
    this.coinSpine.scale.set(scaleData.scale);
    this.coinSpine.zIndex = 10;
    this.coinSpine.alpha = 0.001; // Hidden initially, keeps in GPU

    this.overlaySymbolContainer.addChild(this.coinSpine);

    // ✅ Build animation availability cache (like main game)
    if (this.coinSpine.skeleton?.data?.animations?.length > 0) {
      this.animationAvailability = new Set(
        this.coinSpine.skeleton.data.animations.map((a) => a.name.toLowerCase())
      );
    }

    // Check available animations using cache
    for (const animName of this.animationAvailability) {
      if (animName.includes("land")) this.hasLanding = true;
      if (animName.includes("idle")) this.hasIdle = true;
      if (animName.includes("appear")) this.hasAppear = true;
      if (animName.includes("win")) this.hasWin = true;
    }

    // ✅ Warm up ALL animations (first reel, first symbol only)
    // This compiles shaders for all animation types
    if (this.reelIndex === 0 && this.symbolIndex === 0) {
      this.warmUpSpineAnimation("land");
      this.warmUpSpineAnimation("idle");
      this.warmUpSpineAnimation("appear");
      this.warmUpSpineAnimation("win");
    }
  }

  /**
   * Create multiplier spine instance
   */
  createMultiplierSpineInstance() {
    const spineName = "hns_multiplier";
    const skeletonAlias = `${spineName}-skeleton`;
    const atlasAlias = `${spineName}-atlas`;
    const assets = PIXI.Assets.cache;

    if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
      return null;
    }

    try {
      const spine = Spine.from({ skeleton: skeletonAlias, atlas: atlasAlias });
      spine.name = `multiplier_spine_${this.reelIndex}_${this.symbolIndex}`;
      return spine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize the multiplier spine instance (hidden, ready to show)
   */
  initializeMultiplierSpine() {
    if (!this.overlaySymbolContainer) return;

    this.multiplierSpine = this.createMultiplierSpineInstance();
    if (!this.multiplierSpine) return;

    const scaleData = this.calculateSpineScale(
      this.multiplierSpine,
      this.symbolWidth,
      this.symbolHeight,
      true // Skip animation setup during initialization
    );

    this.multiplierSpine.position.set(scaleData.x, scaleData.y);
    this.multiplierSpine.scale.set(scaleData.scale);
    this.multiplierSpine.zIndex = 20; // Above coin spine (zIndex 10)
    this.multiplierSpine.alpha = 0.001; // Hidden initially, keeps in GPU

    this.overlaySymbolContainer.addChild(this.multiplierSpine);

    // Build animation availability cache
    if (this.multiplierSpine.skeleton?.data?.animations?.length > 0) {
      this.multiplierAnimationAvailability = new Set(
        this.multiplierSpine.skeleton.data.animations.map((a) =>
          a.name.toLowerCase()
        )
      );
    }

    // Warm up multiplier animations (first reel, first symbol only)
    if (this.reelIndex === 0 && this.symbolIndex === 0) {
      this.warmUpMultiplierAnimation("in");
      this.warmUpMultiplierAnimation("idle");
      this.warmUpMultiplierAnimation("out");
    }
  }

  /**
   * Select multiplier animation by type
   */
  selectMultiplierAnimation(type) {
    if (!this.multiplierSpine?.state) return null;

    const cacheKey = type;
    if (this.multiplierAnimationCache.has(cacheKey)) {
      return this.multiplierAnimationCache.get(cacheKey);
    }

    const animations = this.multiplierSpine.skeleton?.data?.animations || [];
    const lowerType = type.toLowerCase();

    // First try exact match
    for (let i = 0; i < animations.length; i++) {
      const anim = animations[i];
      const animName = anim.name.toLowerCase();
      if (animName === lowerType) {
        this.multiplierAnimationCache.set(cacheKey, anim.name);
        return anim.name;
      }
    }

    // Fallback to includes match (for backward compatibility)
    for (let i = 0; i < animations.length; i++) {
      const anim = animations[i];
      const animName = anim.name.toLowerCase();
      if (animName.includes(lowerType)) {
        this.multiplierAnimationCache.set(cacheKey, anim.name);
        return anim.name;
      }
    }

    return null;
  }

  /**
   * Play multiplier sound with debouncing to prevent multiple sounds at once
   * Only plays if enough time has passed since last play
   */
  playMultiplierSoundDebounced() {
    const now = Date.now();
    const timeSinceLastPlay =
      now - HoldAndWinOverlaySymbolController.lastMultiplierSoundTime;

    // Only play if enough time has passed
    if (timeSinceLastPlay >= HoldAndWinOverlaySymbolController.MULTIPLIER_SOUND_DEBOUNCE) {
      soundManager.play(SOUND_NAME.coin_multiplier);
      HoldAndWinOverlaySymbolController.lastMultiplierSoundTime = now;
    }
  }

  /**
   * Warm up multiplier spine animation (shader compilation)
   */
  warmUpMultiplierAnimation(type) {
    if (!this.multiplierSpine?.state) return;

    const animName = this.selectMultiplierAnimation(type);
    if (!animName) return;

    try {
      // Only clear track 0 (multiplier animations), preserve track 1 (tension)
      this.multiplierSpine.state.clearTrack(0);
      this.multiplierSpine.state.addAnimation(0, animName, true, 0);
      this.multiplierSpine.state.update(0.016);

      this.multiplierSpine.alpha = 0.001;

      requestAnimationFrame(() => {
        if (this.multiplierSpine?.state)
          this.multiplierSpine.state.clearTrack(0);
        if (this.multiplierSpine) this.multiplierSpine.alpha = 0.001;
      });
    } catch (error) {
      // Silently ignore warmup errors
    }
  }

  /**
   * Show multiplier animation (in -> idle loop)
   * @param {number} multiplierValue - 2, 3, or 10
   */
  showMultiplier(multiplierValue) {
    if (!this.multiplierSpine || !this.overlaySymbolContainer) {
      return;
    }

    // Only show for valid multiplier values (2, 3, 10)
    if (![2, 3, 10].includes(multiplierValue)) {
      this.hideMultiplier();
      return;
    }

    // Allow re-triggering to ensure multiplier is visible after each spin
    // (The effect will only call this when needed based on current round data)

    // Set skin based on multiplier value
    const skinName = `x${multiplierValue}`;

    if (this.multiplierSpine.skeleton?.data?.skins) {
      const skin = this.multiplierSpine.skeleton.data.findSkin(skinName);
      if (skin) {
        this.multiplierSpine.skeleton.setSkin(skin);
        this.multiplierSpine.skeleton.setSlotsToSetupPose(); // Apply skin attachments
      }
    }

    // Make spine visible
    this.multiplierSpine.alpha = 1;
    this.multiplierSpine.visible = true; // Ensure visible flag is also set
    this.isMultiplierAnimating = true;
    this.currentMultiplierValue = multiplierValue;

    // Play coin multiplier sound (debounced to prevent multiple sounds)
    this.playMultiplierSoundDebounced();

    // Play in -> idle sequence on track 0
    const inAnim = this.selectMultiplierAnimation("in");
    const idleAnim = this.selectMultiplierAnimation("idle");

    if (this.multiplierSpine.state && inAnim && idleAnim) {
      // Only clear track 0 (multiplier animations), preserve track 1 (tension)
      this.multiplierSpine.state.clearTrack(0);
      // Track 0: in -> idle sequence
      this.multiplierSpine.state.addAnimation(0, inAnim, false, 0);
      this.multiplierSpine.state.addAnimation(0, idleAnim, true, 0);
    } else if (this.multiplierSpine.state && idleAnim) {
      // Fallback to just idle if in animation not found
      // Only clear track 0 (multiplier animations), preserve track 1 (tension)
      this.multiplierSpine.state.clearTrack(0);
      this.multiplierSpine.state.addAnimation(0, idleAnim, true, 0);
    }
  }

  /**
   * Hide multiplier animation
   */
  hideMultiplier() {
    if (!this.multiplierSpine) return;

    this.multiplierSpine.alpha = 0.001;
    this.isMultiplierAnimating = false;
    this.currentMultiplierValue = null;

    if (this.multiplierSpine.state) {
      // Only clear track 0 (multiplier animations), preserve track 1 (tension)
      this.multiplierSpine.state.clearTrack(0);
      // Don't call setToSetupPose() - it would reset tension bones on Track 1
    }
  }

  /**
   * Update coin value text display (instant, no animation)
   * @param {number} value - The value to display
   */
  updateCoinValueDisplay(value) {
    if (!this.coinValueBitmapText) return;
    this.coinValueBitmapText.text = formatToCustomLocale(value);
  }

  /**
   * Animate coin value from current value to multiplied value
   * @param {number} multiplierValue - The multiplier to apply
   * @param {number} duration - Animation duration in ms (default 500ms)
   * @returns {Promise} Resolves when animation completes
   */
  animateCoinValueWithMultiplier(multiplierValue, duration = 500) {
    return new Promise((resolve) => {
      if (!this.coinValueBitmapText || !multiplierValue) {
        resolve();
        return;
      }

      // Stop any existing animation
      if (this.coinValueAnimationRef) {
        this.coinValueAnimationRef.pause();
        this.coinValueAnimationRef = null;
      }

      // FIXED: Calculate from source data instead of parsing formatted text
      // This prevents locale-specific formatting issues (e.g., "0,40" vs "0.40")
      const symbolValue = symbolData[this.currentSymbolId]?.value;
      if (!symbolValue || symbolValue === null) {
        resolve();
        return;
      }

      try {
        const state = store.getState();
        const bet = state?.game?.bet || 1;
        const coinValue = parseInt(symbolValue, 10);

        if (isNaN(coinValue) || coinValue <= 0) {
          resolve();
          return;
        }

        // Calculate current and target values from source data
        const currentValue = coinValue * bet;
        const targetValue = currentValue * multiplierValue;
        this.currentCoinValue = currentValue;

        // Animate from current to target value
        this.coinValueAnimationRef = anime({
          targets: this,
          currentCoinValue: targetValue,
          duration: duration,
          easing: "easeOutQuad",
          update: () => {
            this.updateCoinValueDisplay(this.currentCoinValue);
          },
          complete: () => {
            this.currentCoinValue = targetValue;
            this.updateCoinValueDisplay(targetValue);
            this.coinValueAnimationRef = null;
            resolve();
          },
        });
      } catch (error) {
        console.error("[HoldAndWin] Error animating coin value with multiplier:", error);
        resolve();
      }
    });
  }

  /**
   * Play multiplier out animation, then update coin value with multiplied result
   * Used during collection: OUT animation → multiply text → play win animation → ready to hide
   * @param {number} multiplierValue - The multiplier (2, 3, or 10)
   * @returns {Promise} Resolves when out animation completes and text is updated
   */
  applyMultiplierAndAnimate(multiplierValue) {
    return new Promise((resolve) => {
      if (!this.multiplierSpine || !multiplierValue || multiplierValue <= 1) {
        resolve();
        return;
      }

      // Play out animation
      const outAnim = this.selectMultiplierAnimation("out");

      if (this.multiplierSpine.state && outAnim) {
        // Play coin multiplier sound (debounced to prevent multiple sounds)
        this.playMultiplierSoundDebounced();

        // Use setAnimation to immediately replace current animation with out animation
        // This is different from progress bar - multiplier is on track 0 and needs immediate replacement
        this.multiplierSpine.state.setAnimation(0, outAnim, false);

        // Get animation duration to wait for completion
        const animDuration =
          this.multiplierSpine.skeleton.data.animations.find(
            (anim) => anim.name === outAnim
          )?.duration || 0.5;

        // Wait for out animation to complete, then continue
        const timeoutId = setTimeout(() => {
          // Hide multiplier and continue with counter animation
          this.hideMultiplier();

          // Play win animation once (non-looping) while counter animates
          this.showWinAnimation(this.currentSymbolId, false, false);

          this.animateCoinValueWithMultiplier(multiplierValue, 500)
            .then(() => {
              resolve();
            })
            .catch(() => {
              resolve(); // Still resolve to not block collection
            });
        }, animDuration * 1000);

        // Track timeout for cleanup
        this.activeTimeouts.push(timeoutId);
      } else {
        // No out animation, animate value immediately
        // Play win animation once (non-looping) while counter animates
        this.showWinAnimation(this.currentSymbolId, false, false);

        this.animateCoinValueWithMultiplier(multiplierValue, 500).then(() => {
          this.hideMultiplier();
          resolve();
        });
      }
    });
  }

  /**
   * Show landing animation for a coin symbol
   * Matches main game's showLandingAnimation pattern exactly
   */
  showLandingAnimation(symbolId) {
    if (!this.coinSpine || !this.overlaySymbolContainer) return;

    if (!this.hasLanding && !this.hasIdle) {
      return;
    }

    // Check if already showing same animation
    if (
      this.isAnimating &&
      this.currentSymbolId === symbolId &&
      (this.currentAnimationType === "landing" ||
        this.currentAnimationType === "idle")
    ) {
      return;
    }

    // Make spine visible
    this.coinSpine.alpha = 1;
    this.overlaySymbolContainer.alpha = 1;
    this.isAnimating = true;

    // Play landing -> idle sequence (or just idle if no landing)
    if (this.hasLanding && this.hasIdle) {
      const landingAnim = this.selectSpineAnimation("land");
      const idleAnim = this.selectSpineAnimation("idle");

      if (this.coinSpine.state && landingAnim && idleAnim) {
        this.coinSpine.state.clearTracks();
        this.coinSpine.state.addAnimation(0, landingAnim, false, 0);
        this.coinSpine.state.addAnimation(0, idleAnim, true, 0);
      }
    } else if (this.hasIdle) {
      const idleAnim = this.selectSpineAnimation("idle");
      if (this.coinSpine.state && idleAnim) {
        this.coinSpine.state.clearTracks();
        this.coinSpine.state.addAnimation(0, idleAnim, true, 0);
      }
    } else if (this.hasLanding) {
      const landingAnim = this.selectSpineAnimation("land");
      if (this.coinSpine.state && landingAnim) {
        this.coinSpine.state.clearTracks();
        this.coinSpine.state.addAnimation(0, landingAnim, true, 0);
      }
    }

    // Track current animation state
    this.currentAnimationType =
      this.hasLanding && this.hasIdle
        ? "idle"
        : this.hasIdle
        ? "idle"
        : "landing";
    this.currentSymbolId = symbolId;

    // Show coin value text
    this.showCoinValueText(symbolId);
  }

  /**
   * Show win animation for a coin symbol
   * Can be used for win celebrations in Hold & Win
   * @param {string|number} symbolId - The symbol ID
   * @param {boolean} loop - Whether to loop the win animation (default: true)
   * @param {boolean} showText - Whether to show coin value text (default: true)
   */
  showWinAnimation(symbolId, loop = true, showText = true) {
    if (!this.coinSpine || !this.overlaySymbolContainer) return;

    if (!this.hasWin && !this.hasIdle) {
      return;
    }

    // Check if already showing same animation
    if (
      this.isAnimating &&
      this.currentSymbolId === symbolId &&
      this.currentAnimationType === "win"
    ) {
      return;
    }

    // Make spine visible
    this.coinSpine.alpha = 1;
    this.overlaySymbolContainer.alpha = 1;
    this.isAnimating = true;

    // Play win animation (or fallback to idle)
    const winAnim = this.hasWin ? this.selectSpineAnimation("win") : null;
    const idleAnim = this.hasIdle ? this.selectSpineAnimation("idle") : null;

    if (this.coinSpine.state) {
      this.coinSpine.state.clearTracks();
      if (winAnim) {
        if (loop) {
          // Loop the win animation
          this.coinSpine.state.addAnimation(0, winAnim, true, 0);
        } else {
          // Play win once, then transition to idle
          this.coinSpine.state.addAnimation(0, winAnim, false, 0);
          if (idleAnim) {
            this.coinSpine.state.addAnimation(0, idleAnim, true, 0);
          }
        }
      } else if (idleAnim) {
        this.coinSpine.state.addAnimation(0, idleAnim, true, 0);
      }
    }

    // Track current animation state
    this.currentAnimationType = "win";
    this.currentSymbolId = symbolId;

    // Show coin value text (only if requested)
    if (showText) {
      this.showCoinValueText(symbolId);
    }
  }

  /**
   * Show dark animation (coin collected state)
   * @param {string|number} symbolId - The symbol ID
   */
  showDarkAnimation(symbolId) {
    if (!this.coinSpine || !this.overlaySymbolContainer) return;

    // Make spine visible
    this.coinSpine.alpha = 1;
    this.overlaySymbolContainer.alpha = 1;
    this.isAnimating = true;

    // Play dark animation once (non-looping) and stay at final frame
    const darkAnim = this.selectSpineAnimation("dark");

    if (this.coinSpine.state && darkAnim) {
      this.coinSpine.state.clearTracks();
      this.coinSpine.state.setAnimation(0, darkAnim, false);
    }

    // Track current animation state
    this.currentAnimationType = "dark";
    this.currentSymbolId = symbolId;

    // Hide coin value text (coin is collected, no value)
    this.hideCoinValueText();
  }

  /**
   * Show idle animation only
   */
  showIdleAnimation(symbolId) {
    if (!this.coinSpine || !this.overlaySymbolContainer) {
      return;
    }

    if (!this.hasIdle) {
      return;
    }

    // Make spine visible
    this.coinSpine.alpha = 1;
    this.overlaySymbolContainer.alpha = 1;
    this.isAnimating = true;

    const idleAnim = this.selectSpineAnimation("idle");

    if (this.coinSpine.state && idleAnim) {
      this.coinSpine.state.clearTracks();
      this.coinSpine.state.addAnimation(0, idleAnim, true, 0);
    }

    // Track current animation state
    this.currentAnimationType = "idle";
    this.currentSymbolId = symbolId;

    // Show coin value text
    this.showCoinValueText(symbolId);
  }

  /**
   * Hide animation
   * Matches main game's hideWinAnimation pattern with skeleton reset
   */
  hideAnimation() {
    if (!this.coinSpine || !this.overlaySymbolContainer) return;

    this.coinSpine.alpha = 0.001;
    this.overlaySymbolContainer.alpha = 0.001;
    this.isAnimating = false;

    if (this.coinSpine.state) {
      this.coinSpine.state.clearTracks();
      // ✅ Reset skeleton to setup pose (prevents animation artifacts)
      if (this.coinSpine.skeleton?.setToSetupPose) {
        this.coinSpine.skeleton.setToSetupPose();
      }
    }

    // Clear animation state
    this.currentAnimationType = null;
    this.currentSymbolId = null;

    this.hideCoinValueText();
  }

  /**
   * Force deep cleanup (like main game's forceDeepSpineCleanup)
   */
  forceDeepSpineCleanup() {
    if (!this.coinSpine) return;

    if (this.coinSpine.state) {
      this.coinSpine.state.clearTracks();
      if (this.coinSpine.skeleton?.setToSetupPose) {
        this.coinSpine.skeleton.setToSetupPose();
      }
      this.coinSpine.alpha = 0.001;
    }

    if (this.overlaySymbolContainer?.alpha > 0) {
      this.overlaySymbolContainer.alpha = 0.001;
    }

    this.isAnimating = false;
    this.currentAnimationType = null;
    this.currentSymbolId = null;
    this.hideCoinValueText();
  }

  /**
   * Resize animations when symbol size changes
   * ✅ FIXED: Now properly updates coin value text font size and recalculates digit-based scaling
   */
  resizeAnimations(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;

    if (this.coinSpine) {
      const scaleData = this.calculateSpineScale(
        this.coinSpine,
        symbolWidth,
        symbolHeight,
        true // Skip animation setup during resize
      );
      this.coinSpine.position.set(scaleData.x, scaleData.y);
      this.coinSpine.scale.set(scaleData.scale);
    }

    // Resize multiplier spine
    if (this.multiplierSpine) {
      const scaleData = this.calculateSpineScale(
        this.multiplierSpine,
        symbolWidth,
        symbolHeight,
        true // Skip animation setup during resize
      );
      this.multiplierSpine.position.set(scaleData.x, scaleData.y);
      this.multiplierSpine.scale.set(scaleData.scale);
    }

    // ✅ Update coin value text font size on resize and recalculate digit-based scaling
    if (this.coinValueBitmapText) {
      const displayValue = this.coinValueBitmapText.text;

      // Calculate new font size based on updated symbol height
      const fontSize = this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
      this.coinValueBitmapText.fontSize = fontSize;

      // Reset scale first
      this.coinValueBitmapText.scale.set(1);

      // Force update to get accurate measurements with new font size
      this.coinValueBitmapText.updateText();
      this.coinValueBitmapText.dirty = true;

      // Recalculate scaling with new dimensions
      const coinWidth = this.symbolWidth;
      const paddingPercent = 0.12;
      const maxTextWidth = coinWidth * (1 - paddingPercent * 2);
      const actualTextWidth = this.coinValueBitmapText.width;

      // Only apply scaling if text is too wide
      if (actualTextWidth > maxTextWidth) {
        const digitCount = displayValue.replace(/[^0-9]/g, '').length;

        if (digitCount <= 4) {
          const scaleX = maxTextWidth / actualTextWidth;
          this.coinValueBitmapText.scale.x = scaleX;
        } else if (digitCount >= 5 && digitCount <= 8) {
          const scaleX = maxTextWidth / actualTextWidth;
          this.coinValueBitmapText.scale.x = scaleX;
        } else {
          const scale = maxTextWidth / actualTextWidth;
          this.coinValueBitmapText.scale.set(scale);
        }
      }
    }
  }

  /**
   * Cleanup
   * Matches main game's cleanup pattern exactly
   */
  cleanup() {
    // Clear all active timeouts to prevent memory leaks
    this.activeTimeouts.forEach(clearTimeout);
    this.activeTimeouts = [];

    this.stopCoinValueBoneUpdate();
    this.hideCoinValueText();

    if (this.coinSpine) {
      if (this.coinSpine.state) {
        this.coinSpine.state.clearTracks();
        this.coinSpine.state.clearListeners();
      }
      if (this.coinSpine.parent) {
        this.coinSpine.parent.removeChild(this.coinSpine);
      }
      this.coinSpine.destroy({
        children: true,
        texture: false,
        baseTexture: false,
      });
      this.coinSpine = null;
    }

    // Cleanup multiplier spine
    if (this.multiplierSpine) {
      if (this.multiplierSpine.state) {
        this.multiplierSpine.state.clearTracks();
        this.multiplierSpine.state.clearListeners();
      }
      if (this.multiplierSpine.parent) {
        this.multiplierSpine.parent.removeChild(this.multiplierSpine);
      }
      this.multiplierSpine.destroy({
        children: true,
        texture: false,
        baseTexture: false,
      });
      this.multiplierSpine = null;
    }

    this.animationCache.clear();
    this.animationAvailability.clear();
    this.multiplierAnimationCache.clear();
    this.multiplierAnimationAvailability.clear();
    this.isAnimating = false;
    this.currentAnimationType = null;
    this.currentSymbolId = null;
    this.isMultiplierAnimating = false;
    this.currentMultiplierValue = null;
  }
}

export default HoldAndWinOverlaySymbolController;
